import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { RecoveryLocation, RecoveryObject } from '../../api'
import { RecoveryObjectCard } from './RecoveryObjectCard'
import { describeChanges, objKey, type Update } from './location-updates'

/**
 * One column per location, under its clock, listing its recovery objects.
 *
 * The caller hands the objects over already ordered (location-updates orderColumn): updated cards
 * first, newest first, so a change lands at the top of the column instead of flashing somewhere
 * below the fold (Andrew, 2026-09-29). The heading says how many are still unseen, and clicking
 * that count marks the whole column as seen.
 */
export function LocationColumn({
    location,
    objects,
    updates,
    flashing,
    onSeen,
}: {
    location: RecoveryLocation
    objects: RecoveryObject[]
    updates: ReadonlyMap<string, Update>
    flashing: ReadonlySet<string>
    onSeen: (keys: string[]) => void
}) {
    const { t } = useTranslation()
    const unseen = objects.filter((o) => updates.has(objKey(o)))
    const anyFlashing = unseen.some((o) => flashing.has(objKey(o)))

    return (
        <div className="flex w-72 shrink-0 flex-col gap-2" data-testid={`location-column-${location.id}`}>
            <div className="flex items-center gap-2 px-1">
                <span
                    className={cn(
                        'h-2 w-2 shrink-0 rounded-full',
                        unseen.length > 0 ? 'bg-primary' : 'bg-muted-foreground/30',
                        anyFlashing && 'animate-pulse',
                    )}
                    aria-hidden
                />
                <h3 className="truncate text-sm font-semibold text-foreground" title={location.name}>
                    {location.name}
                </h3>
                <span className="text-xs text-muted-foreground">({objects.length})</span>
                {unseen.length > 0 && (
                    <button
                        type="button"
                        onClick={() => onSeen(unseen.map(objKey))}
                        title={t('recoverRequests.locationTracking.markColumnSeen', 'Mark {{location}} as seen', { location: location.name })}
                        className="ml-auto rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/20"
                        data-testid="column-new-count"
                    >
                        {t('recoverRequests.locationTracking.newCount', '{{count}} new', { count: unseen.length })}
                    </button>
                )}
            </div>

            {objects.length === 0 ? (
                <div className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground">
                    {t('recoverRequests.locationTracking.noObjects', 'No objects at this location')}
                </div>
            ) : (
                objects.map((o) => {
                    const u = updates.get(objKey(o))
                    return (
                        <RecoveryObjectCard
                            key={o.id}
                            obj={o}
                            update={u ? { at: u.at, changes: describeChanges(u, o) } : null}
                            flashing={flashing.has(objKey(o))}
                            onSeen={() => onSeen([objKey(o)])}
                        />
                    )
                })
            )}
        </div>
    )
}
